import type { HelpTopic } from '../types';

/**
 * Help for Accounts, Website, Masters and Administration screens.
 * Written for office staff: short sentences, the screen's own labels.
 */
export const adminHelp: HelpTopic[] = [
  // ════════════════════════════════════════════════════════════════════
  // Accounts
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/accounts/vouchers',
    title: 'Vouchers',
    summary:
      'Enter, view, alter and cancel the money entries of a company, the same way as in Tally. Every voucher has a debit side and a credit side, and both must be equal.',
    sections: [
      {
        title: 'Debit (Dr) and credit (Cr) in simple words',
        body: 'Every voucher moves an amount from one ledger to another. Dr is the ledger that receives. Cr is the ledger that gives. The total of Dr lines must be equal to the total of Cr lines.',
        bullets: [
          'Money comes into cash or bank: cash / bank is Dr.',
          'Money goes out of cash or bank: cash / bank is Cr.',
          'An expense (rent, salary, electricity) is Dr.',
          'An income (design fees, interest) is Cr.',
          'A client who must pay us is Dr. A vendor whom we must pay is Cr.',
        ],
      },
      {
        title: 'Which voucher type to use',
        body: 'Pick the type with the tabs at the top of the entry window, or press its key. Each type allows only certain ledgers on each side. The ledger list on a line shows only the ledgers that fit.',
        bullets: [
          'Payment (F5): money goes out. Cr must be a cash or bank ledger. Dr is the expense or the party paid.',
          'Receipt (F6): money comes in. Dr must be a cash or bank ledger. Cr is the income or the party who paid.',
          'Contra (F4): money moves between cash and bank only. Every line is a cash or bank ledger.',
          'Journal (F7): adjustments with no cash or bank, for example depreciation, provisions, or moving an amount from one party to another.',
          '"Cash / bank" means a ledger under Cash-in-Hand, Bank Accounts or Bank OD A/c.',
        ],
      },
      {
        title: 'Examples',
        bullets: [
          'Office rent ₹15,000 paid in cash → Payment. Dr Office Rent 15,000. Cr Cash 15,000.',
          'Design fees ₹50,000 received in HDFC Bank from ABC Developers → Receipt. Dr HDFC Bank 50,000. Cr ABC Developers 50,000.',
          'If the client deducted TDS, the receipt holds only the amount that reached the bank. Enter the TDS part as a Journal: Dr TDS Receivable, Cr ABC Developers.',
          'Cash ₹20,000 withdrawn from HDFC Bank for the office → Contra. Dr Cash 20,000. Cr HDFC Bank 20,000.',
          'Cash ₹30,000 deposited into the bank → Contra. Dr HDFC Bank 30,000. Cr Cash 30,000.',
          '₹1,00,000 transferred from HDFC Bank to SBI → Contra. Dr SBI 1,00,000. Cr HDFC Bank 1,00,000.',
          'Depreciation ₹12,000 on computers for the year → Journal. Dr Depreciation 12,000. Cr Computers 12,000.',
        ],
      },
      {
        title: 'Step by step: enter a voucher',
        steps: [
          'Choose the Company at the top right. The screen remembers your choice.',
          'Click New voucher. The Voucher Entry window opens on Payment.',
          'Click the tab for the type you need (or press F4, F5, F6 or F7).',
          'Enter the Date. Add the Reference no. and Reference date if you have a bill or cheque.',
          'On the first line, choose Dr or Cr, then type a few letters of the ledger name and pick it. Enter the Amount.',
          'Do the same on the second line. Click Add line (or press Alt+N) if you need more lines. A new line already shows the side and amount that will balance the voucher.',
          'Check the totals bar. It must show Balanced.',
          'Write the Narration, for example "Rent for October 2026, receipt no. 45".',
          'Click Save (or press Ctrl+Enter or Ctrl+S). Click Save & new to save and start the next voucher with the same type and date.',
        ],
      },
      {
        title: 'Fields in Voucher Entry',
        fields: [
          { name: 'Date', required: true, help: 'The date of the transaction. It cannot be before the books begin, and it cannot be on or before the lock date.' },
          { name: 'Reference no.', help: 'Bill number, cheque number or UTR. Up to 60 characters. You can search vouchers by it later.' },
          { name: 'Reference date', help: 'The date on the bill or cheque.' },
          { name: 'Dr / Cr', required: true, help: 'The side of this line. Changing the side may clear the ledger if that ledger is not allowed on the new side.' },
          { name: 'Ledger', required: true, help: 'Search by name, code or group. The list shows the group and the current balance. Each ledger can appear only once in a voucher. Inactive ledgers are not offered.' },
          { name: 'Amount', required: true, help: 'Rupees and paise, for example 15000 or 15000.50. Two decimals at most. No minus sign.' },
          { name: 'Line narration', help: 'A note for this line only. Up to 255 characters.' },
          { name: 'Narration', help: 'A note for the whole voucher. Up to 1000 characters. It prints on the voucher and shows in the books.' },
        ],
      },
      {
        title: 'Open, print, alter or cancel',
        steps: [
          'Click any row in the list to open the voucher.',
          'Click Print to see the voucher as a PDF.',
          'Click Alter to change it. You can change the date, references, narration and lines. Click Save alteration.',
          'Click Cancel voucher to remove its effect. Type the Reason and confirm. This cannot be undone.',
          'Click History to see who entered, altered or cancelled it, and when.',
        ],
        bullets: [
          'Alter keeps the voucher number. The type cannot change, and the date cannot move to another financial year. For that, cancel it and enter a new voucher.',
          'A cancelled voucher stays in the list with a Cancelled mark and a line through it. It no longer counts in any balance or report.',
          'If someone else changed the voucher while you were altering it, a yellow message appears. Click Reload to get their version. Your unsaved changes are lost.',
          "If you don't see New voucher, Alter, Cancel voucher or Print, your role doesn't have that action — ask the admin.",
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Numbers are given automatically, per company, type and financial year, with no gaps: PMT/2026-27/00001 (Payment), RCT/… (Receipt), CTR/… (Contra), JRN/… (Journal).',
          'Cash cannot go below zero. If a payment would make a cash ledger negative, saving fails with "Not enough cash". First enter the cash received or the cash withdrawn from the bank. A bank ledger may go negative.',
          'Locked dates: if the books are locked up to a date, no voucher on or before that date can be entered, altered or cancelled. Ask an admin to change the lock date in Books Settings.',
          'Save stays grey until the voucher is correct. The red message at the bottom left tells you what is missing, for example "Debits and credits differ by 500.00."',
          'Vouchers are never deleted. A mistake is altered or cancelled, and the history keeps every change.',
          'Use the From, To, Type, Status and search box (number, reference, narration) to find a voucher. This FY sets the dates to 1 April to today.',
          'The total row at the bottom counts posted vouchers only. Cancelled vouchers are not added.',
        ],
      },
    ],
    related: [
      { label: 'Ledgers', path: '/accounts/ledgers' },
      { label: 'Day Book', path: '/accounts/day-book' },
      { label: 'Cash Book', path: '/accounts/cash-book' },
      { label: 'Books Settings', path: '/accounts/settings' },
    ],
  },
  {
    path: '/accounts/ledgers',
    title: 'Ledgers',
    summary:
      'Ledgers are the accounts that vouchers post to: each bank, cash, client, vendor, expense and income. Each ledger sits under one group, and the group decides how it behaves.',
    sections: [
      {
        title: 'Which group to choose',
        bullets: [
          'Bank account (current or savings) → Bank Accounts.',
          'Overdraft or cash credit account → Bank OD A/c.',
          'Cash box or petty cash → Cash-in-Hand. A ledger named Cash already exists.',
          'Office rent, electricity, telephone, salary, printing → Indirect Expenses (or Direct Expenses if your accountant says so).',
          'Design or consultancy fees → Direct Incomes or Sales Accounts. Bank interest received → Indirect Incomes.',
          'Client (who pays us) → Sundry Debtors.',
          'Vendor or supplier (whom we pay) → Sundry Creditors.',
          'GST, TDS and other taxes → Duties & Taxes.',
          'Partner or owner capital → Capital Account.',
          'Computers, furniture, vehicles, equipment → Fixed Assets.',
          'Loans taken → Secured Loans or Unsecured Loans. Rent deposit paid → Deposits (Asset). Salary advance given → Loans & Advances (Asset).',
        ],
      },
      {
        title: 'Step by step: add a ledger',
        steps: [
          'Choose the Company at the top right.',
          'Click New ledger.',
          'Type the Name, for example "HDFC Bank - Current A/c" or "ABC Developers".',
          'Pick the group in Under group. The line below it shows the nature (Asset, Liability, Income or Expense) and whether it is a cash or bank ledger.',
          'For an asset or liability, enter the Opening balance and choose Dr or Cr.',
          'For a bank ledger, fill in Account no., IFSC and Branch.',
          'Add GSTIN, PAN and Address if you have them.',
          'Click Save. The message "Ledger created." appears and the ledger is in the list.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Name', required: true, help: 'Up to 150 characters. Must be different from every other ledger of this company.' },
          { name: 'Code', help: 'Your own short code, up to 30 characters. Must be unique. You can search ledgers by code in vouchers.' },
          { name: 'Under group', required: true, help: 'The group this ledger belongs to. See "Which group to choose".' },
          { name: 'Opening balance', help: 'The balance as on the books’ beginning date. Rupees and paise, no minus sign. Not shown for income and expense ledgers.' },
          { name: 'Side', help: 'Dr or Cr for the opening balance. It is set for you when you pick the group: Dr for assets, Cr for liabilities.' },
          { name: 'Account no.', help: 'Bank ledgers only.' },
          { name: 'IFSC', help: 'Bank ledgers only. 11 characters: 4 letters, then 0, then 6 letters or digits, for example HDFC0001234.' },
          { name: 'Branch', help: 'Bank ledgers only.' },
          { name: 'GSTIN', help: 'The party’s GST number. Saved in capital letters.' },
          { name: 'PAN', help: 'The party’s PAN. Saved in capital letters.' },
          { name: 'Address', help: 'Up to 500 characters.' },
          { name: 'Active', help: 'Turn off to stop using the ledger in new vouchers. Old vouchers stay as they are.' },
        ],
      },
      {
        title: 'Opening balance rules',
        bullets: [
          'Dr opening: money in bank, cash in hand, amount a client owes us, fixed assets, deposits and advances given.',
          'Cr opening: capital, amount we owe a vendor, loans taken, taxes payable, a bank overdraft.',
          'Income and expense ledgers have no opening balance. They start at nil every financial year on 1 April. What they held last year is shown as "Profit & Loss A/c (previous years)" in the Trial Balance.',
          'Total Dr openings should equal total Cr openings. If not, the Trial Balance shows a "Difference in opening balances" line.',
          'Opening balances cannot change once the books are locked from their beginning date.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Current balance: assets and liabilities show the balance from the beginning. Income and expenses show this financial year only.',
          'Click a ledger name, or the book icon, to open its Ledger Book.',
          'Use the search box (name, code, group) and the Group filter. A group filter also shows the sub-groups under it.',
          'A ledger with vouchers, even cancelled ones, cannot be deleted. Edit it and switch Active off instead.',
          'A ledger with vouchers cannot move to a group of another kind (for example from Bank Accounts to Sundry Debtors, or from an expense group to an asset group).',
          'The Cash ledger (System) cannot be deleted and stays under Cash-in-Hand.',
          "If you don't see New ledger, Edit or Delete, your role doesn't have Edit on Ledgers — ask the admin.",
        ],
      },
    ],
    related: [
      { label: 'Ledger Groups', path: '/accounts/groups' },
      { label: 'Ledger Book', path: '/accounts/ledger-book' },
      { label: 'Vouchers', path: '/accounts/vouchers' },
      { label: 'Trial Balance', path: '/accounts/trial-balance' },
    ],
  },
  {
    path: '/accounts/groups',
    title: 'Ledger Groups',
    summary:
      'The tree of groups that holds the ledgers, like the chart of accounts in Tally. Each company starts with Tally’s 28 reserved groups.',
    sections: [
      {
        title: 'How groups work',
        bullets: [
          'Each group has a nature: Asset, Liability, Income or Expense. A sub-group takes the nature of its parent.',
          'Groups under Cash-in-Hand are cash groups. Groups under Bank Accounts or Bank OD A/c are bank groups. Their ledgers can be used as cash or bank in vouchers.',
          'Reserved groups have a System mark with a lock. They cannot be renamed, moved or deleted. You can add sub-groups under them.',
          'The Ledgers column shows how many ledgers are directly in the group.',
        ],
      },
      {
        title: 'Step by step: add a sub-group',
        steps: [
          'Choose the Company at the top right.',
          'Find the parent group, for example Indirect Expenses.',
          'Click the Add sub-group icon (folder with a plus) on that row.',
          'Type the Name, for example "Office Running Expenses".',
          'Leave Under as it is, or pick another parent. The line below shows the nature it will take.',
          'Click Save. The message "Group created." appears.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Name', required: true, help: 'Up to 100 characters. Must be different from every other group.' },
          { name: 'Under', help: 'The parent group. Choose "Primary (no parent)" for a new top-level group.' },
          { name: 'Nature', required: true, help: 'Only for a primary group: Asset, Liability, Income or Expense.' },
          { name: 'Sort order', help: 'Lower numbers show first. Leave blank to keep the default (1000).' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Use the Primary group button only when no reserved group fits. Most new groups belong under an existing one.',
          'Use the pencil (Rename / move) to change a group you added. A group cannot move under itself or its own sub-groups.',
          'A group whose ledgers already have vouchers cannot move to a group of another kind.',
          'Only an empty group (no sub-groups, no ledgers) can be deleted. Move its ledgers first.',
          'Use Expand all / Collapse all, or the arrow on a row, to open and close the tree.',
          "If you don't see Primary group or the row icons, your role doesn't have Edit on Ledger Groups — ask the admin.",
        ],
      },
    ],
    related: [
      { label: 'Ledgers', path: '/accounts/ledgers' },
      { label: 'Trial Balance', path: '/accounts/trial-balance' },
    ],
  },
  {
    path: '/accounts/day-book',
    title: 'Day Book',
    summary: 'All vouchers of one day, or of a date range, with their Dr and Cr lines. Use it to check the day’s entries.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Choose the Company at the top right.',
          'The Date is today. Use the arrows to go to the previous or next day, or pick a date.',
          'To see more than one day, switch on Range and set From and To.',
          'To see only one type, choose it in Type.',
          'Click a voucher to open it. From there you can print, alter or cancel it if your role allows.',
        ],
      },
      {
        title: 'Reading the Day Book',
        bullets: [
          'Each voucher shows its date, number, type and reference. Below it are its lines: Dr lines show the amount under Debit, Cr lines under Credit.',
          'The narration is shown under the lines.',
          'Cancelled vouchers are shown with a line through them. They are not added to the totals.',
          'The Total row shows the number of posted vouchers and the total debit and credit. Both must be the same.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'The Day Book only shows. To enter a voucher, go to Vouchers.',
          "If a voucher opens but Alter or Cancel voucher is missing, your role doesn't have that action — ask the admin.",
        ],
      },
    ],
    related: [
      { label: 'Vouchers', path: '/accounts/vouchers' },
      { label: 'Cash Book', path: '/accounts/cash-book' },
    ],
  },
  {
    path: '/accounts/cash-book',
    title: 'Cash Book',
    summary: 'The statement of every cash ledger for a period: opening cash, cash received, cash paid and closing cash.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Choose the Company at the top right.',
          'Set From and To. The default is 1 April to today. This FY brings it back.',
          'Leave Cash ledger on "All cash ledgers" to see them all, or pick one.',
          'Read the four boxes: Opening, Received (Dr), Paid (Cr) and Closing.',
          'Below, each cash ledger has its own statement. Click a row to open that voucher.',
        ],
      },
      {
        title: 'Reading a statement',
        bullets: [
          '"To …" rows are money received (Debit). The name after "To" is the ledger on the other side, for example To HDFC Bank for cash withdrawn.',
          '"By …" rows are money paid (Credit). For example By Office Rent.',
          'Balance is the running cash balance after each row. Cash should always be Dr.',
          'Opening balance is the cash on the From date. Closing balance is the cash on the To date. Match it with the cash in the box.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Only posted vouchers count. Cancelled vouchers are left out.',
          'An inactive cash ledger with no balance and no entries is not shown.',
          'Cash cannot go below zero, so the cash balance is never Cr.',
        ],
      },
    ],
    related: [
      { label: 'Bank Book', path: '/accounts/bank-book' },
      { label: 'Vouchers', path: '/accounts/vouchers' },
      { label: 'Day Book', path: '/accounts/day-book' },
    ],
  },
  {
    path: '/accounts/bank-book',
    title: 'Bank Book',
    summary:
      'The statement of every bank ledger (Bank Accounts and Bank OD A/c) for a period: opening, deposits, withdrawals and closing. Use it to match with the bank statement.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Choose the Company at the top right.',
          'Set From and To. The default is 1 April to today. This FY brings it back.',
          'Leave Bank ledger on "All bank ledgers", or pick one bank.',
          'Read the four boxes: Opening, Deposits (Dr), Withdrawals (Cr) and Closing.',
          'Compare each row with the bank statement. Click a row to open the voucher.',
        ],
      },
      {
        title: 'Reading a statement',
        bullets: [
          '"To …" rows are money into the bank (Debit). "By …" rows are money out of the bank (Credit).',
          'A Dr balance is money in the bank. A Cr balance means overdraft (you owe the bank).',
          'When all banks are shown, the boxes at the top add them together.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Only posted vouchers count. Cancelled vouchers are left out.',
          'A missing row usually means the voucher was entered with another date, or as cash by mistake. Check the Day Book.',
        ],
      },
    ],
    related: [
      { label: 'Cash Book', path: '/accounts/cash-book' },
      { label: 'Ledger Book', path: '/accounts/ledger-book' },
      { label: 'Vouchers', path: '/accounts/vouchers' },
    ],
  },
  {
    path: '/accounts/ledger-book',
    title: 'Ledger Book',
    summary: 'The statement of one ledger for a period: opening balance, every entry with a running balance, and the closing balance.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Choose the Company at the top right.',
          'In Ledger, type a few letters and pick the ledger. The list is arranged by group.',
          'Set From and To. The default is 1 April to today.',
          'Read the boxes: Opening, Debit, Credit and Closing.',
          'Click any row to open its voucher.',
        ],
      },
      {
        title: 'Reading the statement',
        bullets: [
          '"To …" is a debit to this ledger. The name after it is the ledger credited on the other side.',
          '"By …" is a credit to this ledger. The name after it is the ledger debited on the other side.',
          'Balance shows the running balance with Dr or Cr after each row.',
          'For a client (Sundry Debtors), Dr means the client still owes us. For a vendor (Sundry Creditors), Cr means we still owe them.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'For an income or expense ledger, From and To must be in the same financial year (1 April to 31 March). Its opening is counted from 1 April.',
          'For other ledgers the opening includes the opening balance and every entry before the From date.',
          'The ledger and dates are kept in the page address. You can bookmark it or share the link.',
          'You can also reach this screen by clicking a ledger in Ledgers or in the Trial Balance.',
        ],
      },
    ],
    related: [
      { label: 'Ledgers', path: '/accounts/ledgers' },
      { label: 'Trial Balance', path: '/accounts/trial-balance' },
      { label: 'Vouchers', path: '/accounts/vouchers' },
    ],
  },
  {
    path: '/accounts/trial-balance',
    title: 'Trial Balance',
    summary:
      'Every ledger’s opening, debits, credits and closing, arranged under its groups. Total debits must equal total credits.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Choose the Company at the top right.',
          'Set From and To inside one financial year (1 April to 31 March). The default is 1 April to today.',
          'Check the mark next to the dates: Balanced ✓ (green) or Not balanced (red).',
          'Use the arrows or Expand all / Collapse all to open or close groups.',
          'Click a ledger name to open its Ledger Book for the same dates.',
        ],
      },
      {
        title: 'Reading it',
        bullets: [
          'Opening Dr / Cr: the balance on the From date. Transactions Debit / Credit: the entries between From and To. Closing Dr / Cr: the balance on the To date.',
          'Bold rows are groups with the total of everything under them.',
          'Assets and expenses usually close in Dr. Liabilities, capital and income usually close in Cr.',
          'Cash and bank should be Dr (a bank OD can be Cr). A client in Cr usually means an advance received.',
          'Switch off Opening or Transactions to show fewer columns. Switch on Zero balances to also show ledgers with nothing in them.',
        ],
      },
      {
        title: 'The two special rows',
        bullets: [
          '"Profit & Loss A/c (previous years)": income and expense ledgers start at nil each 1 April. Their result from earlier years is shown here in one line, so the totals still agree.',
          '"Difference in opening balances" with the mark "Check opening balances": the Dr and Cr opening balances you entered in Ledgers do not agree. Correct the opening balances in Ledgers until this line goes away.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'A trial balance covers one financial year. If From and To are in different years, you get an error. Make two reports instead.',
          'Only posted vouchers count. Cancelled vouchers are left out.',
          'If it shows Not balanced and there is no "Difference in opening balances" line, ask an admin to run the Integrity check in Books Settings.',
          "If ledger names are not links, your role doesn't have Ledger Book — ask the admin.",
        ],
      },
    ],
    related: [
      { label: 'Ledger Book', path: '/accounts/ledger-book' },
      { label: 'Ledgers', path: '/accounts/ledgers' },
      { label: 'Books Settings', path: '/accounts/settings' },
    ],
  },
  {
    path: '/accounts/settings',
    title: 'Books Settings',
    summary:
      'Admin only. Set the date the books of a company begin, lock old dates so nobody can change them, and check that the stored balances are correct.',
    sections: [
      {
        title: 'Step by step: lock a closed period',
        steps: [
          'Choose the Company at the top right.',
          'In Locked up to, pick the last date to lock, for example 31-03-2026 after the year is closed.',
          'Read the line below it: "No voucher dated on or before … can be entered, altered or cancelled."',
          'Click Save. The message "Books saved." appears.',
          'To unlock, click Clear and then Save. Reset puts back the saved dates without saving.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          {
            name: 'Books begin on',
            required: true,
            help: 'All opening balances are as on this date. It starts as 1 April of the year the books were opened. You can change it only until the first voucher is entered. After that it is fixed.',
          },
          {
            name: 'Locked up to',
            help: 'Vouchers dated on or before this date cannot be entered, altered or cancelled. Leave empty to keep every date open. It cannot be before the books begin.',
          },
        ],
      },
      {
        title: 'Integrity check',
        steps: ['Click Check in the Integrity box.', 'Wait for the result.'],
        bullets: [
          'Green "All good" means every voucher balances and every stored balance matches its entries.',
          'A red box lists the problems: vouchers whose debits and credits differ, ledger balances that differ from their postings, or voucher lines out of step with their voucher.',
          'Report any red result to the software team with a screenshot. Do not try to fix it with new vouchers.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'If the lock date is on or after the books’ beginning date, opening balances in Ledgers can no longer change.',
          'Each company has its own books, its own beginning date and its own lock date.',
          'Only an admin can save these dates. Other users cannot open this screen.',
        ],
      },
    ],
    related: [
      { label: 'Trial Balance', path: '/accounts/trial-balance' },
      { label: 'Vouchers', path: '/accounts/vouchers' },
      { label: 'Company Master', path: '/company-master' },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // Website
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/update-data',
    title: 'News & Updates',
    summary:
      'Manage what the public website shows: the projects in "Website\'s Data" and the articles in the "News billboard".',
    sections: [
      {
        title: 'Step by step: add a project to the website',
        steps: [
          'Click Add Project.',
          'Fill in the form (see Project fields below).',
          'Upload 2 to 5 photos.',
          'Click Publish project. The list reloads with the new project.',
        ],
      },
      {
        title: 'Project fields',
        fields: [
          { name: 'Project type', required: true, help: 'Residential, Commercial, Institutional, Industrial or Other.' },
          { name: 'Project name', required: true, help: '3 to 60 characters.' },
          { name: 'Location', required: true, help: 'Pick a location already used, or type a new one.' },
          { name: 'Builder name', required: true, help: '3 to 60 characters.' },
          { name: 'Builder contact number', help: 'Exactly 10 digits, without +91 or spaces.' },
          { name: 'Total area of project', required: true, help: 'In sq.ft. Numbers only, at least 1.' },
          { name: 'Status', required: true, help: 'In progress or Completed.' },
          { name: 'Mention status', required: true, help: 'Shown when Status is In progress. A short line, for example "Slab work on 5th floor".' },
          { name: 'Year of completion', help: 'Shown when Status is Completed. Pick the year. Future years are not allowed.' },
          { name: 'Description', required: true, help: '50 to 1000 characters about the project.' },
          { name: 'Tags', required: true, help: 'Type a tag and press Enter or Space. Add as many as you need. Old tags are suggested.' },
          { name: 'Upload photos', required: true, help: '2 to 5 photos. Each up to 5 MB. Each must be wide in 16:9 shape (for example 1920 × 1080), or it is refused.' },
        ],
      },
      {
        title: 'Step by step: add a news article',
        steps: [
          'In News billboard, click Add news article.',
          'Fill in Title, Content and Reference Link.',
          'Click Upload photo and choose one photo.',
          'Click Publish article. The message "News article submitted successfully" appears.',
        ],
      },
      {
        title: 'News fields',
        fields: [
          { name: 'Title', required: true, help: 'The heading of the article.' },
          { name: 'Content', required: true, help: '50 to 1000 characters. The counter shows how many you have typed.' },
          { name: 'Reference Link', required: true, help: 'The full web address of the source, starting with https://.' },
          { name: 'Upload photo', required: true, help: 'One photo, up to 5 MB, wide 16:9 shape.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Use Edit on a project ("Section 1", "Section 2" …) or on an article to change it. Click Publish again to save.',
          'Delete asks you to confirm. A deleted project or article is removed from the website and cannot be brought back.',
          'If the form does not save, read the red list at the top of the form. It shows every field that needs fixing.',
        ],
      },
    ],
    related: [
      { label: 'Contact Submissions', path: '/contact-submissions' },
      { label: 'Files', path: '/files' },
    ],
  },
  {
    path: '/contact-submissions',
    title: 'Contact Us Submissions',
    summary:
      'Messages sent from the Contact Us page of the website: sales enquiries, 3rd party alliance requests and feedback. Read them and set their status.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Pick a tab at the top: Sales Enquiries, 3rd Party Alliance or Feedback.',
          'Read the rows. Move the mouse over a long text (Enquiry, Offering, Feedback, Office Address) to read all of it.',
          'Click a link under Attachments to open a file the person sent.',
          'Contact the person by Email, Contact or WhatsApp.',
          'Change Status to In Progress when you start, and to Closed when done. It saves at once and shows "Status updated".',
        ],
      },
      {
        title: 'What each tab shows',
        bullets: [
          'Sales Enquiries: Name, Email, Contact, WhatsApp, Enquiry, Contact Back Via (email or phone with the preferred time), Attachments, Received, Status.',
          '3rd Party Alliance: Company, Point of Contact, contact details, Website, Business Type, Offering, Office Address, Attachments, Received, Status.',
          'Feedback: Name, contact details, Type (thumbs up for positive, thumbs down for negative), Feedback, Attachments, Received, Status.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Status can be New, In Progress or Closed. New messages start as New.',
          'Click the Name, Company, Type or Received heading to sort.',
          'Job applications are not here. They go to Recruitment → Candidates.',
          'If the status change fails, the old status comes back and an error message shows.',
        ],
      },
    ],
    related: [{ label: 'News & Updates', path: '/update-data' }],
  },

  // ════════════════════════════════════════════════════════════════════
  // Masters
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/company-master',
    title: 'Company Master',
    summary:
      'The firm’s own companies. A project is taken under one of them, bills print its details, and each company has its own accounts books.',
    sections: [
      {
        title: 'Step by step: add a company',
        steps: [
          'Click Add Company.',
          'Type the Company Name. Fill in the address, contact, GST and bank details.',
          'Upload the logo and the signature & stamp image for bills.',
          'Keep Active on.',
          'Click Save. The message "Company created" appears.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Company Name', required: true, help: 'The full legal name, as printed on bills.' },
          { name: 'Address', help: 'Full office address.' },
          { name: 'Contact No. 1 / Contact No. 2', help: 'Phone numbers.' },
          { name: 'Email', help: 'Office email.' },
          { name: 'Website', help: 'For example https://www.example.in.' },
          { name: 'GST No.', help: '15 characters, for example 27ABCDE1234F1Z5. A wrong format is refused.' },
          { name: 'Registration No.', help: 'LLP, company or other registration number.' },
          { name: 'City / State / Country', help: 'Country is India by default.' },
          { name: 'Active', help: 'Switch off to hide the company from the Add Project list. Old projects are not changed.' },
          { name: 'Bill Prefix', help: 'Starts every bill number, for example SCPL → SCPL/INV/MOTE/7/2025-26.' },
          { name: 'Registration Label', help: 'How the Registration No. is named on bills, for example "LLP no."' },
          { name: 'Logo (bill letterhead)', help: 'PNG or JPG, wide. Printed top right on bills. Click Upload or Replace.' },
          { name: 'Signature & stamp', help: 'One PNG or JPG with both signature and stamp. Printed above the signatory.' },
          { name: 'Signatory Name', help: 'For example R. V. Shirke.' },
          { name: 'Signatory Registration', help: 'For example P. M. C. Registration No.: 181 (CAT. A).' },
          { name: 'Bank Name / Bank Branch / Bank Account Number / Bank IFSC', help: 'Printed on bills for payment.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Only an admin can add, edit or delete companies. Others can see the list, but saving shows "You do not have access to this area."',
          'A company with projects cannot be deleted. Switch Active off instead.',
          'The accounts screens list these companies in the Company box at the top.',
          'Use the pencil to edit and the bin to delete.',
        ],
      },
    ],
    related: [
      { label: 'Contact Masters', path: '/contact-masters' },
      { label: 'Books Settings', path: '/accounts/settings' },
    ],
  },
  {
    path: '/contact-masters',
    title: 'Contact Masters',
    summary: 'The list of outside people we work with: clients, architects, builders, owners and supervisors. Project forms pick names from here.',
    sections: [
      {
        title: 'Step by step: add a contact',
        steps: [
          'Click the tab: Client, Architect, Builder, Owner or Supervisor.',
          'Click Add Client (or Add Architect, and so on).',
          'Type the Name and the other details you have.',
          'Click Save. The new contact appears in the list.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Name', required: true, help: 'The person’s or firm’s name.' },
          { name: 'Company Name', help: 'Client only. The client’s company.' },
          { name: 'Building Name', help: 'Owner only. The building they own.' },
          { name: 'Mobile / Phone / WhatsApp / Email', help: 'Client, Architect, Builder and Owner. Fill in what you have.' },
          { name: 'Office Address / Address', help: 'Full address.' },
          { name: 'GST Number', help: 'Client only. 15 characters.' },
          { name: 'Work Order', help: 'Client only. The work order number or note.' },
          { name: 'Contact', help: 'Supervisor only. One phone number or other contact.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Look through the list (sort by Name) before adding, so you do not create the same person twice.',
          'Use the pencil to edit. The bin deletes the contact for ever after you confirm.',
          'Click a column heading with arrows (ID, Name, Company, Building) to sort.',
        ],
      },
    ],
    related: [
      { label: 'Company Master', path: '/company-master' },
      { label: 'Reference Data', path: '/reference-data' },
    ],
  },
  {
    path: '/reference-data',
    title: 'Reference Data',
    summary:
      'The short lists that drop-down boxes use across the panel: work activities, project types, localities, priorities, statuses, financial years and more.',
    sections: [
      {
        title: 'Step by step: add an item',
        steps: [
          'Click the tab of the list you want, for example Locality or Project Type. Use the arrows at the ends to see more tabs.',
          'Click Add … (for example Add Locality).',
          'Fill in the fields. Fields with * are required.',
          'Click Save. The item is now offered in the forms that use this list.',
        ],
      },
      {
        title: 'Common fields',
        fields: [
          { name: 'Code', required: true, help: 'A short unique key, small letters with _ in place of spaces, for example new_job. Do not change it once used.' },
          { name: 'Label', required: true, help: 'The text people see in the drop-down.' },
          { name: 'Description', help: 'A note on when to use it.' },
          { name: 'Sort Order', help: 'A number. Lower numbers show first in the drop-down.' },
          { name: 'Active', help: 'Switch off to hide the item from new entries. Old records keep it.' },
        ],
      },
      {
        title: 'Lists with their own fields',
        fields: [
          { name: 'Work Activity', help: 'Work Activity Name (required).' },
          { name: 'Time Entry Category', help: 'Category (required): work outside a task, for example Meeting or Training.' },
          { name: 'Type of Work Activity', help: 'Work Activity (required, pick one), Type of Work Activity (required), and switches Requires structural drawing and Requires shuttering / framing.' },
          { name: 'Locality', help: 'Locality Name (required). Code may be left blank; it is made from the name.' },
          { name: 'Priority', help: 'Code, Label, Level (a number) and Colour (hex), for example #EF4444.' },
          { name: 'Shift Type', help: 'Code, Label, Start Time and End Time.' },
          { name: 'Authority', help: 'Sanctioning authority: Code, Name (required), Short Name, City, State.' },
          { name: 'Status Category', help: 'Code (for example work_job, job_task), Label, Description.' },
          { name: 'Status', help: 'Status Category ID (the ID number from the Status Category tab), Code, Label, Colour, Sort Order, Terminal state (the last step), Default for category.' },
          { name: 'Financial Year', help: 'Label (for example 2025-26), Start Date, End Date, Active.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Prefer switching Active off to deleting. A deleted item may break old records that use it.',
          'Work Activity, Type of Work Activity and Time Entry Category can be changed only by an admin.',
          'Use the pencil to edit and the bin to delete. Delete asks you to confirm.',
        ],
      },
    ],
    related: [
      { label: 'Contact Masters', path: '/contact-masters' },
      { label: 'Company Master', path: '/company-master' },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // Administration
  // ════════════════════════════════════════════════════════════════════
  {
    path: '/users',
    title: 'Users',
    summary: 'Admin only. Create logins for staff, link each login to an employee, and give it a role.',
    sections: [
      {
        title: 'Step by step: add a user',
        steps: [
          'Click Add User.',
          'Type the Full Name and a Username.',
          'In Employee Number, type the code or name and pick the employee.',
          'Pick the Department.',
          'Fill in Mobile and Email.',
          'Choose the Role. The role decides which menus the person sees.',
          'Type a Password of at least 6 characters. Give it to the person and ask them to change it.',
          'Click Save. The message "User created" appears.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Full Name', required: true, help: 'The person’s name as shown across the panel.' },
          { name: 'Username', required: true, help: 'What the person types to sign in. Keep it simple, without spaces.' },
          { name: 'Employee Number', help: 'Search by code (SDLLP-xx) or name. This links the login to that employee’s tasks and schedule.' },
          { name: 'Department', help: 'Pick from the departments list, or type a new value.' },
          { name: 'Mobile', help: '10-digit mobile number.' },
          { name: 'Email', help: 'Work email.' },
          { name: 'Role', help: 'Admin, HR, Accounts, Manager / Analyst, Employee, Site Engineer, or a role you made in Roles & Menu Access.' },
          { name: 'Password', required: true, help: 'Required for a new user. At least 6 characters. When editing, leave it blank to keep the current password, or type a new one to reset it.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'To reset a forgotten password, click the pencil, type a new Password and Save.',
          'Users cannot be deleted, because their tasks, attendance and history depend on them. Change the role to keep them out.',
          'A user with NO ROLE sees no menu. Give every login a role.',
          'A second login with the same Employee Number acts as the same employee. Use it only to give one person a second role.',
        ],
      },
    ],
    related: [
      { label: 'Roles & Menu Access', path: '/roles' },
      { label: 'Change Password', path: '/reset-password' },
    ],
  },
  {
    path: '/roles',
    title: 'Roles & Menu Access',
    summary:
      'Admin only. Decide which menus each role sees and what it may do on each screen (View, Create, Edit, Delete, Approve, Export). Then give logins their role.',
    sections: [
      {
        title: 'Step by step: change what a role can do',
        steps: [
          'In ROLES on the left, click the role, for example Accounts.',
          'Stay on the Menu access tab.',
          'Tick View on a sub-menu to show it in that role’s menu. Untick View to hide it.',
          'Tick the actions the role may use, for example Create and Edit on Vouchers. Ticking an action also ticks View.',
          'Tick the box on a main menu row (for example Accounts) to give all its sub-menus with every action. Untick it to remove the whole menu.',
          'Check the yellow Unsaved changes mark, then click Save. Click Undo to go back to the saved ticks.',
          'Ask the users of that role to reload the page or sign in again to see the new menu.',
        ],
      },
      {
        title: 'Step by step: create a role',
        steps: [
          'Click New role.',
          'Type the Name, for example "Accounts Assistant". The Code is filled for you.',
          'Add a Description so others know what the role is for.',
          'Click Save. The new role is selected.',
          'Use Copy from role to start from an existing role’s ticks, change what you need, and click Save.',
        ],
        fields: [
          { name: 'Name', required: true, help: 'The role name people see.' },
          { name: 'Code', required: true, help: 'Small letters, digits and _ only. It is stored on each login and cannot change later.' },
          { name: 'Description', help: 'One line on what this role does.' },
        ],
      },
      {
        title: 'What the actions mean',
        bullets: [
          'View: the sub-menu appears and the screen opens. A "–" means the screen has no such action.',
          'Vouchers: Create = enter; Edit = alter; Delete = cancel; Export = print.',
          'Ledgers, Ledger Groups: Edit = add, change and delete.',
          'Projects: Edit = add and edit projects, assign work.',
          'Attendance Register: Edit = correct days, import biometric CSV.',
          'Leave Approvals, Reimbursement: Approve = approve or reject.',
          'Holidays, Pay Profiles, Payroll Rules: Edit = change them.',
          'Letters & Certificates: Create = issue letters to others.',
          'Salary Processing: Edit = calculate; Approve = mark salaries paid; Export = payslip PDFs, bank advice and bank statement.',
          'Under each sub-menu name, the grey line explains its actions.',
        ],
      },
      {
        title: 'Give a login its role (Users tab)',
        steps: [
          'Select the role on the left and open the Users tab. The number shows how many logins have it.',
          'In "Add a login to …", search the person and pick them. They move to this role.',
          'To move someone out, change the Role box next to their name, or pick No role.',
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'Admin always has every menu and every action. It cannot be changed.',
          'Entries marked Admin only (Users, Roles & Menu Access, Books Settings) cannot be given to other roles.',
          'Built-in roles (Admin, HR, Accounts, Manager / Analyst, Employee, Site Engineer) cannot be deleted. They can be renamed with the pencil.',
          'A role that still has logins cannot be deleted. Move its users first.',
          'You cannot take Admin away from your own login.',
          'The yellow message at the top lists logins with no role. They see no menu at all. Give them a role on a role’s Users tab.',
        ],
      },
    ],
    related: [
      { label: 'Users', path: '/users' },
      { label: 'Books Settings', path: '/accounts/settings' },
    ],
  },
  {
    path: '/files',
    title: 'Files',
    summary: 'Upload files to the server and get their File ID, or open any stored file by its ID.',
    sections: [
      {
        title: 'Step by step: upload',
        steps: [
          'Click Upload Files, or drag files onto the dotted box. You can choose many files at once.',
          'Wait until each file shows the message "… uploaded".',
          'The table shows each file with its File ID, Name, Type and time.',
          'Click Copy file ID to copy the number, or Open file to view it.',
        ],
      },
      {
        title: 'Step by step: open a file by ID',
        steps: ['Type the number in Open file by ID.', 'Click Open. The file opens in a new tab.'],
      },
      {
        title: 'Good to know',
        bullets: [
          'Images, video and documents are accepted.',
          'The table shows only files uploaded in this visit. After you leave or reload the page it is empty again. Note the File ID if you need it later.',
          'Files cannot be listed or deleted from this screen.',
          'Other forms (photos, contracts, payment proofs) store files by this File ID.',
        ],
      },
    ],
    related: [{ label: 'News & Updates', path: '/update-data' }],
  },
  {
    path: '/reset-password',
    title: 'Change Password',
    summary: 'Change your own password. You need your current password. Reached from Change Password in the sidebar or Reset Password on the login page.',
    sections: [
      {
        title: 'Step by step',
        steps: [
          'Check the Username. When you are signed in it is filled for you.',
          'Type your Current Password.',
          'Type the New Password, then type it again in Confirm Password.',
          'Click Reset Password.',
          'You see "Password changed. Please sign in again." Sign in with the new password.',
        ],
      },
      {
        title: 'Fields',
        fields: [
          { name: 'Username', required: true, help: 'Your login name.' },
          { name: 'Current Password', required: true, help: 'The password you use now. Click the eye to show it.' },
          { name: 'New Password', required: true, help: 'At least 6 characters. It must be different from the current one.' },
          { name: 'Confirm Password', required: true, help: 'The same new password again.' },
        ],
      },
      {
        title: 'Good to know',
        bullets: [
          'If you forgot your current password, this screen cannot help. Ask the admin to set a new one in Users.',
          '"Old password is incorrect" means the Current Password is wrong. Check Caps Lock.',
          'After the change you are signed out on this browser. Sign in again.',
          'Back to Dashboard (or Back to Login) leaves without changing anything.',
        ],
      },
    ],
    related: [{ label: 'Users', path: '/users' }],
  },
];
